import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { CUSTOM_BUTTON_SIZE, CustomButton } from 'src/components/CustomButton';
import { CustomMarkdown } from 'src/components/CustomMarkdowm';
import CustomTextField from 'src/components/CustomTextField';
import ErrorPanel from 'src/components/ErrorPanel';
import { verifySSOEmail } from 'src/graphql/user';
import { loginWithSSO } from 'src/utils/lib/oauth';

import { useStyles } from './useStyles';

export const SSOLogin = ({ loading, onStart, onSuccess, onError, error }) => {
  const styles = useStyles();

  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = useCallback(
    async (e) => {
      try {
        e.preventDefault();
        if (!email) {
          onError({ message: 'Please enter your email address' });
          return;
        }

        const result = await verifySSOEmail(email);
        if (!result.valid) {
          onError({ message: 'SSO is not enabled for this email' });
          return;
        }
        setProcessing(true);
        onStart();

        const { token } = await loginWithSSO(email);
        onSuccess({ token });
      } catch (e) {
        onError(e);
      } finally {
        setProcessing(false);
      }
    },
    [email, onStart, onSuccess, onError]
  );

  const handleEmailChange = useCallback(
    (event) => {
      onError('');
      setEmail(event.target.value);
    },
    [onError]
  );

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-testid="SSOLogin"
    >
      {error && (
        <ErrorPanel
          classes={{ root: styles.errorPanel }}
          type="warning"
          text={<CustomMarkdown text={error} />}
        />
      )}

      <CustomTextField
        autoFocus
        className={styles.field}
        id="email"
        value={email}
        onChange={handleEmailChange}
        error={!!error}
        label="Email"
        margin="normal"
        variant="outlined"
        disabled={loading}
        fullWidth
        data-testid="SSOLogin__Email"
      />

      <CustomButton
        className={styles.button}
        loading={loading || processing}
        disabled={loading || processing}
        size={CUSTOM_BUTTON_SIZE.large}
        type="submit"
        data-testid="SSOLogin__Button"
        fullWidth
      >
        Log in
      </CustomButton>
    </form>
  );
};

SSOLogin.propTypes = {
  classes: PropTypes.shape({}),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onStart: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

SSOLogin.defaultProps = {
  classes: null,
  loading: null,
  error: null,
};
